import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { opsApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';
import type {
  OpsCdnConnectionResult,
  OpsRebindCredentialPayload,
  OpsRavenAccount,
  OpsRebindCredentialResult,
  OpsVEdgeOrg,
} from '@/api/operations';

/**
 * Operations org hooks — /internal/ops/orgs/* + /internal/ops/dashboard +
 * service lifecycle toggle (activate / deactivate).
 * All endpoints are employee-auth (UMA RPT via opsApi).
 *
 * API contract (BE shipped, verified in ops/types.go + ops/orgs.go + ops/dashboard.go):
 *   GET  /internal/ops/dashboard              → DashboardResponse
 *   GET  /internal/ops/orgs                   → OrgListItem[] (server-paginated)
 *   GET  /internal/ops/orgs/:orgId            → OrgDetail
 *   GET  /internal/ops/orgs/:orgId/teams      → OrgTeamRow[]
 *   GET  /internal/ops/orgs/:orgId/projects   → OrgProjectRow[]
 *   POST /internal/ops/services/:id/activate  → { message, result }
 *   POST /internal/ops/services/:id/deactivate → { message, result }
 */

/* ── Types ─────────────────────────────────────────────────────────────── */

/** One day in the 30-day activation trend. */
export interface ActivationTrendPoint {
  date: string;   // "YYYY-MM-DD"
  count: number;
}

/** One entry in the top-10 orgs by service count. */
export interface TopOrgRow {
  organization_id: string;
  service_count: number;
  customer: {
    customer_id: number;
    customer_name: string;
    customer_company_name?: string;
  } | null;
}

/**
 * Response from GET /internal/ops/dashboard.
 * Field names verified against modules/service/ops/types.go DashboardResponse.
 */
export interface OpsDashboard {
  /** category → status → count (same shape as SummaryResponse.by_category). */
  by_category: Record<string, Record<string, number>>;
  total_domains: number;
  total_orgs: number;
  activation_trend: ActivationTrendPoint[];
  top_orgs: TopOrgRow[];
}

/** Per-category service counts for an org. */
export interface OrgServiceCounts {
  /** category → status → count, e.g. { cdn: { active: 3, terminated: 1 } }. */
  by_category: Record<string, Record<string, number>>;
  total: number;
}

/** One row from GET /internal/ops/orgs. */
export interface OpsOrgListItem {
  organization_id: string;
  customer: {
    customer_id: number;
    customer_name: string;
    customer_company_name?: string;
  } | null;
  services: OrgServiceCounts;
  teams_count: number;
  projects_count: number;
  domain_count: number;
}

/** Full org detail from GET /internal/ops/orgs/:orgId. */
export interface OpsOrgDetail {
  organization_id: string;
  customer: {
    customer_id: number;
    customer_name: string;
    customer_company_name?: string;
  } | null;
  services: OrgServiceCounts;
  teams_count: number;
  projects_count: number;
  domain_count: number;
}

/** One team row from GET /internal/ops/orgs/:orgId/teams. */
export interface OrgTeamRow {
  id: string;
  team_name: string;
  members_count: number;
  created_at: string;
}

/** One project row from GET /internal/ops/orgs/:orgId/projects. */
export interface OrgProjectRow {
  id: string;
  name: string;
  teams_count: number;
  resources_count: number;
  created_at: string;
}

/** Query params for GET /internal/ops/orgs. */
export interface OpsOrgsParams {
  q?: string;
  page?: number;
  per_page?: number;
}

/* ── Query keys ─────────────────────────────────────────────────────────── */

export const opsOrgKeys = {
  dashboard: () => ['ops', 'dashboard'] as const,
  orgs: (params: OpsOrgsParams) => ['ops', 'orgs', params] as const,
  org: (orgId: string) => ['ops', 'org', orgId] as const,
  orgTeams: (orgId: string) => ['ops', 'org', orgId, 'teams'] as const,
  orgProjects: (orgId: string) => ['ops', 'org', orgId, 'projects'] as const,
  serviceToggle: (id: string) => ['ops', 'service', id, 'toggle'] as const,
};

/* ── Helpers ────────────────────────────────────────────────────────────── */

/** Sum all status counts for one category key. */
export function orgCategoryTotal(
  counts: OrgServiceCounts | undefined,
  category: string,
): number {
  const byStatus = counts?.by_category?.[category];
  if (!byStatus) return 0;
  return Object.values(byStatus).reduce((acc, n) => acc + n, 0);
}

/** Sum all status counts across all categories in the dashboard by_category map. */
export function dashboardServicesTotal(
  byCategory: Record<string, Record<string, number>> | undefined,
): number {
  if (!byCategory) return 0;
  return Object.values(byCategory).reduce((acc, statuses) => {
    return acc + Object.values(statuses).reduce((s, n) => s + n, 0);
  }, 0);
}

/** Sum one category across all statuses from the dashboard by_category map. */
export function dashboardCategoryTotal(
  byCategory: Record<string, Record<string, number>> | undefined,
  category: string,
): number {
  const byStatus = byCategory?.[category];
  if (!byStatus) return 0;
  return Object.values(byStatus).reduce((acc, n) => acc + n, 0);
}

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/**
 * `GET /internal/ops/dashboard` — platform-wide stats, activation trend,
 * top orgs. Server-cached 5 min; staleTime matches so we don't hammer the
 * endpoint on every mount.
 */
export function useOpsDashboard() {
  return useQuery({
    queryKey: opsOrgKeys.dashboard(),
    queryFn: async (): Promise<OpsDashboard> => {
      const res = await opsApi.get<{ result: OpsDashboard }>('/internal/ops/dashboard');
      return res.data.result;
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * `GET /internal/ops/orgs` — org list, server-paginated.
 */
export function useOpsOrgs(params: OpsOrgsParams = {}) {
  const query = qs.stringify(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')),
  );
  return useQuery({
    queryKey: opsOrgKeys.orgs(params),
    queryFn: async () => {
      const url = `/internal/ops/orgs${query ? `?${query}` : '?page=1&per_page=20'}`;
      const res = await opsApi.get<ResponseArray<OpsOrgListItem> & { pagination?: unknown }>(url);
      return res.data;
    },
  });
}

/**
 * `GET /internal/ops/orgs/:orgId` — full org detail.
 */
export function useOpsOrg(orgId: string) {
  return useQuery({
    queryKey: opsOrgKeys.org(orgId),
    queryFn: async (): Promise<OpsOrgDetail> => {
      const res = await opsApi.get<{ result: OpsOrgDetail }>(`/internal/ops/orgs/${orgId}`);
      return res.data.result;
    },
    enabled: Boolean(orgId),
  });
}

/**
 * `GET /internal/ops/orgs/:orgId/teams` — teams for one org.
 */
export function useOpsOrgTeams(orgId: string) {
  return useQuery({
    queryKey: opsOrgKeys.orgTeams(orgId),
    queryFn: async (): Promise<OrgTeamRow[]> => {
      const res = await opsApi.get<{ results: OrgTeamRow[] }>(
        `/internal/ops/orgs/${orgId}/teams`,
      );
      return res.data.results ?? [];
    },
    enabled: Boolean(orgId),
  });
}

/**
 * `GET /internal/ops/orgs/:orgId/projects` — projects for one org.
 */
export function useOpsOrgProjects(orgId: string) {
  return useQuery({
    queryKey: opsOrgKeys.orgProjects(orgId),
    queryFn: async (): Promise<OrgProjectRow[]> => {
      const res = await opsApi.get<{ results: OrgProjectRow[] }>(
        `/internal/ops/orgs/${orgId}/projects`,
      );
      return res.data.results ?? [];
    },
    enabled: Boolean(orgId),
  });
}

/* ── Mutations (toggle) ─────────────────────────────────────────────────── */

/** Payload for activate / deactivate mutations. reason is required. */
export interface TogglePayload {
  serviceId: string;
  reason: string;
}

/**
 * `POST /internal/ops/services/:id/activate` — restore a terminated service.
 * Invalidates the service detail + services list on success.
 */
export function useOpsActivateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceId, reason }: TogglePayload) => {
      const res = await opsApi.post<{ message: string; result?: unknown }>(
        `/internal/ops/services/${serviceId}/activate`,
        { reason },
      );
      return res.data;
    },
    onSuccess: (_data, { serviceId }) => {
      // Invalidate service detail + services list so UI reflects new status.
      qc.invalidateQueries({ queryKey: ['ops', 'service', serviceId] });
      qc.invalidateQueries({ queryKey: ['ops', 'services'] });
    },
  });
}

/**
 * `POST /internal/ops/services/:id/deactivate` — soft-terminate a service.
 * Invalidates the service detail + services list on success.
 */
export function useOpsDeactivateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceId, reason }: TogglePayload) => {
      const res = await opsApi.post<{ message: string; result?: unknown }>(
        `/internal/ops/services/${serviceId}/deactivate`,
        { reason },
      );
      return res.data;
    },
    onSuccess: (_data, { serviceId }) => {
      qc.invalidateQueries({ queryKey: ['ops', 'service', serviceId] });
      qc.invalidateQueries({ queryKey: ['ops', 'services'] });
    },
  });
}

/**
 * `POST /internal/ops/services/:id/cdn/connection` — connect an Eagle CDN
 * service to the console: mint a per-customer account + credential for a zone
 * still on the shared reseller key. Idempotent. Invalidates the service detail
 * + the fleet connections list so the connected status refreshes.
 */
export function useOpsConnectCdnService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      serviceId,
      reason,
      force,
    }: {
      serviceId: string;
      reason?: string;
      /** Re-mint the key even when the service is already connected. */
      force?: boolean;
    }) => {
      const res = await opsApi.post<{ message: string; result: OpsCdnConnectionResult }>(
        `/internal/ops/services/${serviceId}/cdn/connection`,
        reason || force ? { reason: reason ?? '', force: force === true } : undefined,
      );
      return res.data.result;
    },
    onSuccess: (_data, { serviceId }) => {
      qc.invalidateQueries({ queryKey: ['ops', 'service', serviceId] });
      qc.invalidateQueries({ queryKey: ['ops', 'services'] });
      qc.invalidateQueries({ queryKey: ['ops', 'cdn', 'connections'] });
    },
  });
}

/**
 * `GET /internal/ops/raven/orgs?q=` — vEdge-side organizations (via the shared
 * Raven reseller login) for the re-bind dialog's org picker. Capped at 100 rows
 * server-side; `q` filters by id, name or slug.
 */
export function useOpsRavenOrgs(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['ops', 'raven', 'orgs', q],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<OpsVEdgeOrg[]> => {
      const res = await opsApi.get<{ results: OpsVEdgeOrg[] }>(
        `/internal/ops/raven/orgs?${qs.stringify(q ? { q } : {})}`,
      );
      return res.data.results ?? [];
    },
  });
}

/**
 * `GET /internal/ops/raven/accounts/check?email=` — does this email already have
 * a vEdge login (and in which vEdge org)? Tells the dialog whether provisioning
 * will create the account or reset its password.
 */
export function useOpsRavenAccountCheck(email: string, enabled: boolean) {
  return useQuery({
    queryKey: ['ops', 'raven', 'account', email],
    enabled: enabled && email.trim() !== '',
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<OpsRavenAccount> => {
      const res = await opsApi.get<{ result: OpsRavenAccount }>(
        `/internal/ops/raven/accounts/check?${qs.stringify({ email })}`,
      );
      return res.data.result;
    },
  });
}

/**
 * `POST /internal/ops/services/:id/credential/rebind` — replace the (org-shared)
 * credential of a Raven service, or of every Raven service of its org. Tech-gated.
 */
export function useOpsRebindRavenCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      serviceId,
      ...body
    }: OpsRebindCredentialPayload & { serviceId: string }) => {
      const res = await opsApi.post<{ message: string; result: OpsRebindCredentialResult }>(
        `/internal/ops/services/${serviceId}/credential/rebind`,
        body,
      );
      return res.data.result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ops', 'service'] });
      qc.invalidateQueries({ queryKey: ['ops', 'services'] });
    },
  });
}
